import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ReactCountryFlag from 'react-country-flag'
import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, TextField } from '@mui/material'
import i18next from 'i18next'

function PreferredLanguageField({
  preferredLanguage,
  handleSelectedLanguage,
  selectedLanguage,
  languageOptions,
}) {
  const { t } = useTranslation()
  const NONE_OPTION = t('None')
  const [displayValue, setDisplayValue] = useState(null)
  const getCountryCode = (languageCode) => {
    const languageToCountryMap = {
      'en-US': 'US',
      'es-ES': 'ES',
      'ja-JP': 'JP',
      'ko-KR': 'KR',
      'nl-NL': 'NL',
      'de-DE': 'DE',
      'pl-PL': 'PL',
      'pt-PT': 'PT',
      'fr-FR': 'FR',
      'it-IT': 'IT',
    }
    return languageToCountryMap[languageCode] || ''
  }

  useEffect(() => {
    if (preferredLanguage) {
      setDisplayValue(languageOptions[preferredLanguage])
    } else {
      setDisplayValue(null)
    }
  }, [preferredLanguage, languageOptions])

  const handleChange = (event, newValue) => {
    if (newValue === NONE_OPTION || !newValue) {
      setDisplayValue(NONE_OPTION)
      handleSelectedLanguage(event, null)
      return
    }

    const languageCode = Object.keys(languageOptions).find(
      (key) => languageOptions[key] === newValue,
    )

    if (languageCode) {
      setDisplayValue(newValue)
      handleSelectedLanguage(event, languageCode)
    }
  }

  const renderOption = (props, option) => {
    if (option === NONE_OPTION) {
      return (
        <Box component="li" {...props}>
          {NONE_OPTION}
        </Box>
      )
    }

    const languageKey = Object.keys(languageOptions).find(
      (key) => languageOptions[key] === option,
    )

    return (
      <Box
        key={languageKey}
        component="li"
        sx={{ '& > svg': { mr: 2, flexShrink: 0 } }}
        {...props}
      >
        <ReactCountryFlag
          countryCode={getCountryCode(languageKey)}
          svg
          style={{ marginRight: 8, marginBottom: 3 }}
        />
        {option}
      </Box>
    )
  }

  const renderInput = (params) => (
    <TextField
      {...params}
      placeholder={t('select-an-option-user')}
      size="small"
      InputProps={{
        ...params.InputProps,
        startAdornment: selectedLanguage ? (
          <ReactCountryFlag
            countryCode={getCountryCode(selectedLanguage)}
            svg
            style={{
              marginRight: 8,
              marginLeft: 10,
              height: '1.5em',
              width: '1.5em',
            }}
          />
        ) : null,
      }}
    />
  )

  return (
    <div data-testid="PreferredLanguage">
      {
        (t('label-pref-lang'),
        (
          <Autocomplete
            disablePortal
            id="preferred-language"
            options={[NONE_OPTION, ...Object.values(languageOptions)]}
            getOptionLabel={(option) => option}
            value={displayValue}
            onChange={handleChange}
            renderOption={renderOption}
            renderInput={renderInput}
          />
        ))
      }
    </div>
  )
}

PreferredLanguageField.propTypes = {
  preferredLanguage: PropTypes.string,
  languageOptions: PropTypes.objectOf(PropTypes.string),
  handleSelectedLanguage: PropTypes.func.isRequired,
  selectedLanguage: PropTypes.string,
}

PreferredLanguageField.defaultProps = {
  preferredLanguage: '',
  selectedLanguage: '',
  languageOptions: {
    'en-US': i18next.t('language-english'),
    'es-ES': i18next.t('language-spanish'),
    'ja-JP': i18next.t('language-japanese'),
    'ko-KR': i18next.t('language-korean'),
    'nl-NL': i18next.t('language-nederlands'),
    'de-DE': i18next.t('language-german'),
    'pl-PL': i18next.t('language-polish'),
    'pt-PT': i18next.t('language-portuguese'),
    'fr-FR': i18next.t('language-french'),
    'it-IT': i18next.t('language-italian'),
  },
}

export default PreferredLanguageField
